// Campaigns.js

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  useOutletContext,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  MenuItem,
  Typography,
  Box,
  Checkbox,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControlLabel,
  Tooltip,
  Card,
  CardMedia,
  CardContent,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import axios from "axios";
import { ROLE_USER, normalizeRole, isAdminRole } from "../../utils/userRoles";
import { resolvePlanLimit } from "../../config/billingPlans";
import juice from "juice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import BlockIcon from "@mui/icons-material/Block";

import EditIcon from "@mui/icons-material/Edit";
import GavelIcon from "@mui/icons-material/Gavel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GridViewIcon from "@mui/icons-material/GridView";
import EmailIcon from "@mui/icons-material/Email";
import GroupIcon from "@mui/icons-material/Group";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Icons for responsive preview
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import TabletMacIcon from "@mui/icons-material/TabletMac";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";

// Import the TermsAgreementDialog component
import TermsAgreementDialog from "../TermsAgreementDialog";
import DnsPropagationCheckSimple from "../DnsPropagationCheckSimple";
import PageSnackbars from "../common/PageSnackbars";
import { usePageSnackbar } from "../common/usePageSnackbar";
import {
  acme,
  acmePageSectionCardSx,
  acmeDialogPaperSx,
  acmeDraftCardSx,
  acmeInfoCardActiveSx,
  acmeBillingSectionContentSx,
} from "../../theme/acmeTheme";
import {
  PageShell,
  PageHeader,
  PageBlockTitle,
  Card as UiCard,
  CardContent as UiCardContent,
  Button as AcmeButton,
  Chip as AcmeChip,
  Input,
  Select as AcmeSelect,
  Dialog as UiDialog,
  DialogTitleText,
  DialogBody,
  DialogFooter,
  TableActionsMenu,
  TableActionsMenuButton,
  MenuItem as UiMenuItem,
} from "../ui";

// Site brand color palette (Acme tokens)
const theme = {
  primary: acme.colors.brand,
  primaryHover: acme.colors.brandHover,
  secondary: "#64748b",
  success: acme.colors.brand,
  warning: "#f59e0b", // Amber
  error: "#ef4444", // Red
  background: "#f8fafc", // Light gray background
  surface: "#ffffff",
  surfaceHover: "#f1f5f9",
  text: {
    primary: "#1e293b",
    secondary: "#64748b",
    muted: "#94a3b8",
  },
  border: acme.colors.border,
  shadow: acme.shadow.sm,
};

const sectionIconSx = {
  fontSize: "1.25rem",
  color: acme.colors.mutedForegroundSolid,
  flexShrink: 0,
};
const checkboxSx = {
  color: acme.colors.brand,
  "&.Mui-checked": { color: acme.colors.brand },
};

// Template Select Dialog Component
const TemplateSelectDialog = ({ open, onClose, templates, onSelect }) => {
  const [templatePreview, setTemplatePreview] = useState(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDevice, setPreviewDevice] = useState("desktop");

  const handlePreview = (template) => {
    setTemplatePreview(template);
    setPreviewDialogOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: acmeDialogPaperSx,
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.border}`,
            pb: 2,
            fontSize: "1.5rem",
            fontWeight: 600,
            color: theme.text.primary,
          }}
        >
          Choose Email Template
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 3,
              mt: 1,
            }}
          >
            {templates.length > 0 ? (
              templates.map((template) => (
                <Card
                  key={template._id}
                  sx={{
                    position: "relative",
                    height: "280px",
                    cursor: "pointer",
                    border: `1px solid ${theme.border}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow:
                        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                      borderColor: acme.colors.brand,
                    },
                  }}
                  onClick={() => onSelect(template)}
                >
                  <CardMedia
                    sx={{
                      height: "200px",
                      position: "relative",
                      overflow: "hidden",
                      backgroundColor: theme.background,
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: "1200px",
                        height: "1800px",
                        transform: "translate(-50%, -50%) scale(0.13)",
                        transformOrigin: "center center",
                        backgroundColor: "#fff",
                        pointerEvents: "none",
                        marginTop: "80px",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: prepareTemplate(
                          template.html,
                          template.css,
                          "desktop",
                          true,
                        ),
                      }}
                    />
                  </CardMedia>
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      noWrap
                      title={template.name}
                      sx={{
                        fontWeight: 500,
                        color: theme.text.primary,
                        mb: 1,
                      }}
                    >
                      {template.name}
                    </Typography>
                    <AcmeButton
                      size="sm"
                      variant="ghost"
                      startIcon={<VisibilityIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePreview(template);
                      }}
                    >
                      Preview
                    </AcmeButton>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  py: 6,
                }}
              >
                <Typography color={theme.text.secondary}>
                  No templates available
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.border}` }}>
          <AcmeButton variant="outline" onClick={onClose}>
            Cancel
          </AcmeButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxWidth: "1200px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">
            {templatePreview?.name || "Template Preview"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              color={previewDevice === "desktop" ? "primary" : "default"}
              onClick={() => setPreviewDevice("desktop")}
              title="Desktop View"
              size="small"
            >
              <DesktopMacIcon />
            </IconButton>
            <IconButton
              color={previewDevice === "tablet" ? "primary" : "default"}
              onClick={() => setPreviewDevice("tablet")}
              title="Tablet View"
              size="small"
            >
              <TabletMacIcon />
            </IconButton>
            <IconButton
              color={previewDevice === "mobile" ? "primary" : "default"}
              onClick={() => setPreviewDevice("mobile")}
              title="Mobile View"
              size="small"
            >
              <PhoneIphoneIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              overflow: "auto",
              backgroundColor: "#f5f5f5",
              p: 2,
            }}
          >
            <Box
              sx={{
                width:
                  previewDevice === "mobile"
                    ? "375px"
                    : previewDevice === "tablet"
                      ? "768px"
                      : "100%",
                maxWidth: previewDevice === "desktop" ? "1200px" : undefined,
                backgroundColor: "#fff",
                borderRadius: previewDevice !== "desktop" ? "8px" : "4px",
                boxShadow:
                  previewDevice !== "desktop"
                    ? "0 4px 20px rgba(0,0,0,0.1)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                overflow: "hidden",
                minHeight: "600px",
              }}
              dangerouslySetInnerHTML={{
                __html: templatePreview?.html
                  ? prepareTemplate(
                      templatePreview.html,
                      templatePreview.css,
                      previewDevice,
                      true,
                    )
                  : "",
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <AcmeButton
            variant="outline"
            onClick={() => setPreviewDialogOpen(false)}
          >
            Close
          </AcmeButton>
          <AcmeButton
            onClick={() => {
              setPreviewDialogOpen(false);
              onSelect(templatePreview);
            }}
          >
            Select
          </AcmeButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Enhanced template preparation with mobile styles
const prepareTemplate = (
  html,
  css,
  device = "desktop",
  isPreviewMode = false,
) => {
  if (!html) return html;

  // Обработка изображений для email совместимости
  const processImagesForEmail = (html) => {
    // В режиме preview оставляем изображения как есть
    if (isPreviewMode) {
      return html;
    }

    // Современные email клиенты поддерживают base64 изображения
    // Оставляем изображения как есть для лучшего отображения
    // Если нужно, можно добавить fallback для старых клиентов
    return html;
  };

  // Clean and enhanced mobile-responsive CSS for MailsballEditorPro templates
  const embedMobileStyles = (html, css) => {
    const mobileCSS = `
      <style>
        /* Universal reset for mobile preview */
        ${
          device !== "desktop"
            ? `
        * {
          box-sizing: border-box !important;
        }
        
        /* Force all containers to full width */
        div[style*="display: flex"],
        div[style*="display:flex"],
        table, tbody, tr, td {
          display: block !important;
          width: 100% !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        
        /* Reset all column-style elements */
        div[style*="flex: 1"],
        div[style*="flex:1"],
        div[style*="width: 50%"],
        div[style*="width: 33.33%"],
        div[style*="width: 25%"],
        div[style*="width:50%"],
        div[style*="width:33.33%"],
        div[style*="width:25%"],
        div[style*="width: 49%"],
        div[style*="width: 32%"],
        div[style*="width: 24%"],
        div[style*="width:49%"],
        div[style*="width:32%"],
        div[style*="width:24%"] {
          flex: none !important;
          width: 100% !important;
          margin-bottom: 15px !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        /* Images responsive */
        img { 
          max-width: 100% !important; 
          height: auto !important; 
          display: block !important;
        }
        
        /* Remove all horizontal padding from containers */
        div[style*="padding:"] {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }
        
        /* Override any margin auto centering */
        div[style*="margin: 0 auto"],
        div[style*="margin:0 auto"] {
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        `
            : ""
        }

        /* MailsballEditorPro Mobile Responsive Styles */
        @media only screen and (max-width: 600px) {
          /* Universal box-sizing */
          * {
            box-sizing: border-box !important;
          }
          
          /* Force all flex containers to stack vertically */
          div[style*="display: flex"],
          div[style*="display:flex"] {
            display: block !important;
            flex-direction: column !important;
            align-items: stretch !important;
            width: 100% !important;
          }
          
          /* Target all column-style elements with comprehensive selectors */
          div[style*="flex: 1"],
          div[style*="flex:1"],
          div[style*="width: 50%"],
          div[style*="width: 33.33%"],
          div[style*="width: 25%"],
          div[style*="width:50%"],
          div[style*="width:33.33%"],
          div[style*="width:25%"],
          div[style*="width: 49%"],
          div[style*="width: 32%"],
          div[style*="width: 24%"],
          div[style*="width:49%"],
          div[style*="width:32%"],
          div[style*="width:24%"] {
            flex: none !important;
            width: 100% !important;
            margin-bottom: 15px !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            float: none !important;
            clear: both !important;
          }
          
          /* Reduce gaps on mobile */
          div[style*="gap: 20px"],
          div[style*="gap:20px"] {
            gap: 10px !important;
          }
          
          div[style*="gap: 15px"],
          div[style*="gap:15px"] {
            gap: 8px !important;
          }
          
          div[style*="gap: 12px"],
          div[style*="gap:12px"] {
            gap: 6px !important;
          }
          
          /* Table-based columns - stack cells */
          table,
          tbody,
          tr,
          td {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
             padding-left: 0 !important;
             padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          
          /* Images - responsive */
          img {
            max-width: 100% !important;
            height: auto !important;
            display: block !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          /* Product blocks - stack image and content */
          div[style*="flex-direction: row"] {
            flex-direction: column !important;
          }
          
          /* Buttons - mobile friendly */
          a[style*="padding"],
          button[style*="padding"] {
            display: inline-block !important;
            width: auto !important;
            min-width: 120px !important;
            text-align: center !important;
            margin: 10px 0 !important;
            padding-left: 16px !important;
            padding-right: 16px !important;
            box-sizing: border-box !important;
          }
          
          /* Text adjustments */
          h1 {
            font-size: 24px !important;
            line-height: 1.2 !important;
          }
          
          h2 {
            font-size: 20px !important;
            line-height: 1.3 !important;
          }
          
          h3 {
            font-size: 18px !important;
            line-height: 1.3 !important;
          }
          
          /* Container padding reset - only vertical padding */
          div[style*="padding: 20px"],
          div[style*="padding:20px"] {
            padding: 15px 0 !important;
          }
          
          div[style*="padding: 15px"],
          div[style*="padding:15px"] {
            padding: 10px 0 !important;
          }
          
          /* Universal horizontal padding reset */
          div[style*="padding:"],
          div[style*="padding-left:"],
          div[style*="padding-right:"] {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Email container */
          .email-container {
            max-width: 100% !important;
            padding: 10px 0 !important;
          }
          
          /* Override any max-width constraints */
          div[style*="max-width"] {
            max-width: 100% !important;
          }
          
          /* Remove any margin centering */
          div[style*="margin: 0 auto"],
          div[style*="margin:0 auto"] {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }

        /* Tablet adjustments - similar to mobile for columns */
        @media only screen and (max-width: 768px) and (min-width: 601px) {
          /* Universal box-sizing */
          * {
            box-sizing: border-box !important;
          }
          
          /* Force all flex containers to stack vertically on tablet too */
          div[style*="display: flex"],
          div[style*="display:flex"] {
            display: block !important;
            flex-direction: column !important;
            align-items: stretch !important;
            width: 100% !important;
          }
          
          div[style*="flex: 1"],
          div[style*="flex:1"],
          div[style*="width: 50%"],
          div[style*="width: 33.33%"],
          div[style*="width: 25%"],
          div[style*="width:50%"],
          div[style*="width:33.33%"],
          div[style*="width:25%"],
          div[style*="width: 49%"],
          div[style*="width: 32%"],
          div[style*="width: 24%"],
          div[style*="width:49%"],
          div[style*="width:32%"],
          div[style*="width:24%"] {
            flex: none !important;
            width: 100% !important;
            margin-bottom: 15px !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            float: none !important;
            clear: both !important;
          }
          
          div[style*="gap: 20px"],
          div[style*="gap:20px"] {
            gap: 15px !important;
          }
          
          /* Table-based columns – stack on tablet too */
          table,
          tbody,
          tr,
          td {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          
          /* Universal horizontal padding reset */
          div[style*="padding:"],
          div[style*="padding-left:"],
          div[style*="padding-right:"] {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Email container */
          .email-container {
            max-width: 100% !important;
            padding: 15px 0 !important;
          }
          
          /* Override any max-width constraints */
          div[style*="max-width"] {
            max-width: 100% !important;
          }
          
          /* Remove any margin centering */
          div[style*="margin: 0 auto"],
          div[style*="margin:0 auto"] {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
      </style>
    `;

    // Insert mobile CSS into the head section
    if (html.includes("<head>")) {
      return html.replace("</head>", `${mobileCSS}</head>`);
    } else if (html.includes("<html>")) {
      return html.replace("<html>", `<html><head>${mobileCSS}</head>`);
    } else {
      return `<head>${mobileCSS}</head>${html}`;
    }
  };

  // First apply inline CSS if available
  let processedHtml = html;
  if (css) {
    try {
      processedHtml = juice.inlineContent(html, css);
    } catch (error) {
      console.warn("Error inlining CSS:", error);
      processedHtml = html;
    }
  }

  // Then embed mobile-responsive styles
  const htmlWithMobileStyles = embedMobileStyles(processedHtml, css);

  // Finally, process images for email compatibility
  return processImagesForEmail(htmlWithMobileStyles);
};

const formFieldsStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const formSectionsStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const campaignNameHeroSx = {
  p: { xs: 3, sm: 4.5 },
  borderRadius: acme.radius.lg,
  border: `1px solid ${acme.colors.border}`,
  backgroundColor: acme.colors.muted,
  boxShadow: acme.shadow.lg,
};

const campaignNameInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: acme.radius.md,
    fontSize: "1.0625rem",
    backgroundColor: acme.colors.background,
    minHeight: 56,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
    "& fieldset": { borderColor: acme.colors.border },
    "&:hover fieldset": { borderColor: acme.colors.mutedForegroundSolid },
    "&.Mui-focused fieldset": {
      borderColor: acme.colors.foreground,
      borderWidth: 1,
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.9375rem",
  },
  "& .MuiInputBase-input": {
    py: 1.75,
    px: 1.5,
  },
};

const campaignNameInnerSx = (expanded) => ({
  maxWidth: expanded ? "100%" : 520,
  mx: "auto",
  textAlign: expanded ? "left" : "center",
  width: "100%",
  transition: "max-width 0.25s ease",
});

const sectionHeadingSx = {
  fontWeight: 600,
  fontSize: "0.9375rem",
  color: acme.colors.foreground,
};

const accordionSx = {
  mb: 0,
  border: `1px solid ${acme.colors.border}`,
  borderRadius: `${acme.radius.md} !important`,
  boxShadow: "none",
  backgroundColor: acme.colors.background,
  "&:before": { display: "none" },
  "&.Mui-expanded": { mb: 0 },
  "& .MuiAccordionSummary-root": {
    minHeight: 48,
    px: 2.5,
  },
  "& .MuiAccordionSummary-content": {
    my: 0.75,
  },
  "& .MuiAccordionDetails-root": {
    px: 2.5,
    pb: 2.5,
    pt: 0,
  },
  "& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root": {
    color: acme.colors.mutedForegroundSolid,
  },
};

const SectionStatusIcon = ({ complete }) =>
  complete ? (
    <CheckCircleIcon
      sx={{ fontSize: "1.125rem", color: acme.colors.brand, flexShrink: 0 }}
    />
  ) : (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `2px solid ${acme.colors.border}`,
        flexShrink: 0,
      }}
    />
  );

const AccordionSectionSummary = ({ icon, title, complete, action = null }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      pr: 0.5,
      gap: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: 1,
        minWidth: 0,
        gap: 1.5,
      }}
    >
      {icon}
      <Typography sx={sectionHeadingSx}>{title}</Typography>
    </Box>
    {action}
    <SectionStatusIcon complete={complete} />
  </Box>
);

const CampaignTemplatePreviewDialog = ({
  open,
  onClose,
  html,
  css,
  previewDevice,
  setPreviewDevice,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{
      sx: {
        minHeight: "70vh",
        maxHeight: "90vh",
      },
    }}
  >
    <DialogTitle sx={{ borderBottom: `1px solid ${theme.border}`, pb: 2 }}>
      Template Preview
    </DialogTitle>
    <DialogContent sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 3,
          p: 1,
          backgroundColor: theme.background,
          border: `1px solid ${theme.border}`,
          borderRadius: 1,
        }}
      >
        {[
          { device: "desktop", icon: <DesktopMacIcon />, label: "Desktop" },
          { device: "tablet", icon: <TabletMacIcon />, label: "Tablet" },
          { device: "mobile", icon: <PhoneIphoneIcon />, label: "Mobile" },
        ].map(({ device, icon, label }) => (
          <Tooltip key={device} title={label}>
            <IconButton
              onClick={() => setPreviewDevice(device)}
              sx={{
                mx: 0.5,
                borderRadius: 1,
                backgroundColor:
                  previewDevice === device ? acme.colors.brand : "transparent",
                color:
                  previewDevice === device ? "white" : theme.text.secondary,
                "&:hover": {
                  backgroundColor:
                    previewDevice === device
                      ? acme.colors.brandHover
                      : theme.surfaceHover,
                },
              }}
            >
              {icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: theme.background,
          borderRadius: 1,
          p: 2,
          border: `1px solid ${theme.border}`,
          minHeight: 420,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            width:
              previewDevice === "mobile"
                ? 375
                : previewDevice === "tablet"
                  ? 768
                  : "100%",
            maxWidth: previewDevice === "desktop" ? 900 : undefined,
            backgroundColor: "#fff",
            borderRadius: 1,
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            overflow: "hidden",
            minHeight: 360,
          }}
          dangerouslySetInnerHTML={{
            __html: html
              ? prepareTemplate(html, css, previewDevice, true)
              : `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:360px;color:${theme.text.muted};text-align:center;padding:24px;">
                  <div style="font-size:2rem;margin-bottom:0.5rem;">📧</div>
                  <div style="font-weight:500;margin-bottom:0.25rem;">No template selected</div>
                  <div style="font-size:0.875rem;color:${theme.text.secondary};">Choose a template in Design to preview it here</div>
                </div>`,
          }}
        />
      </Box>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <AcmeButton variant="outline" onClick={onClose}>
        Close
      </AcmeButton>
    </DialogActions>
  </Dialog>
);

const Campaigns = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const emptyEmailData = {
    campaignName: "",
    groups: [],
    senderName: "",
    senderEmail: "",
    senderAvatar: "",
    subject: "",
    message: "",
    css: "",
    files: [],
  };
  const [emailData, setEmailData] = useState(emptyEmailData);

  const [groups, setGroups] = useState([]);
  const [verifiedEmails, setVerifiedEmails] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [, setBillingHistory] = useState([]);
  const { user } = useOutletContext(); // ????????? user ?? Outlet Context
  const { setSuccessMessage, setErrorMessage, snackbarProps } =
    usePageSnackbar();

  // ????? ????? ??? ????????????? ??????? (??? ??????????)
  const [previewDevice, setPreviewDevice] = useState("desktop");

  // Add new state for terms agreement
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [billingInfo, setBillingInfo] = useState(null);

  const [templateSelectOpen, setTemplateSelectOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    sender: false,
    recipients: false,
    subject: false,
    design: false,
    legal: false,
  });
  const [designMenuAnchor, setDesignMenuAnchor] = useState(null);
  const [emailContentDialogOpen, setEmailContentDialogOpen] = useState(false);
  const [attachmentsDialogOpen, setAttachmentsDialogOpen] = useState(false);
  const [templatePreviewDialogOpen, setTemplatePreviewDialogOpen] =
    useState(false);
  const [draftId, setDraftId] = useState(null);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const skipNextDraftSave = useRef(false);

  // DNS Propagation state
  const [dnsPropagationStatus, setDnsPropagationStatus] = useState(null);
  const [showDnsPropagationWarning, setShowDnsPropagationWarning] =
    useState(false);

  const handleEditTemplate = () => {
    // Navigate to GrapesEditor with current template data
    navigate("/templates/editor", {
      state: {
        template: {
          html: emailData.message,
          css: emailData.css,
          name: "Campaign Template",
        },
      },
    });
  };

  useEffect(() => {
    fetchGroups();
    fetchVerifiedEmails();
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const loadDraftIntoForm = useCallback(
    (draft) => {
      skipNextDraftSave.current = true;
      setDraftId(draft._id);
      setEmailData({
        campaignName: draft.campaignName || "",
        groups: draft.draftGroups || [],
        senderName: draft.senderName || "",
        senderEmail: draft.senderEmail || "",
        senderAvatar: draft.senderAvatar || "",
        subject: draft.draftSubject || "",
        message: draft.draftMessage || "",
        css: draft.draftCss || "",
        files: [],
      });
      setSelectedTemplateId(draft.draftTemplateId || "");
      setDraftLoaded(true);
      setPendingDraft(null);
      if (searchParams.get("draft")) {
        setSearchParams({});
      }
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get("/api/campaigns/draft");
        if (cancelled || !res.data) return;
        const draft = res.data;
        const draftParam = searchParams.get("draft");
        if (draftParam && String(draft._id) === String(draftParam)) {
          loadDraftIntoForm(draft);
        } else {
          setPendingDraft(draft);
        }
      } catch (err) {
        console.error("Failed to fetch campaign draft:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load draft once on mount
  }, []);

  useEffect(() => {
    if (skipNextDraftSave.current) {
      skipNextDraftSave.current = false;
      return undefined;
    }
    if (!emailData.campaignName?.trim()) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await axios.put("/api/campaigns/draft", {
          draftId: draftId || undefined,
          campaignName: emailData.campaignName,
          groups: emailData.groups,
          senderName: emailData.senderName,
          senderEmail: emailData.senderEmail,
          senderAvatar: emailData.senderAvatar,
          subject: emailData.subject,
          message: emailData.message,
          css: emailData.css,
          templateId: selectedTemplateId,
        });
        setDraftId(res.data._id);
        setDraftLoaded(true);
        setPendingDraft(null);
      } catch (err) {
        console.error("Failed to save campaign draft:", err);
      }
    }, 900);

    return () => clearTimeout(timer);
  }, [emailData, selectedTemplateId, draftId]);

  const handleDiscardDraft = async (id) => {
    try {
      await axios.delete(`/api/campaigns/draft/${id}`);
      setPendingDraft(null);
      if (draftId === id) {
        setDraftId(null);
        setDraftLoaded(false);
        setEmailData(emptyEmailData);
        setSelectedTemplateId("");
      }
      setSuccessMessage("Draft discarded.");
    } catch (err) {
      console.error("Failed to discard draft:", err);
      setErrorMessage("Failed to discard draft.");
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get("/api/contacts/groups");
      setGroups(response.data);
    } catch (error) {
      console.error("Failed to fetch groups", error);
    }
  };

  // State for subscription info and plan purchased date
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isSubscription, setIsSubscription] = useState(false);
  const [planPurchasedDate, setPlanPurchasedDate] = useState(null);

  useEffect(() => {
    axios
      .get("/api/billing/current-plan", { withCredentials: true })
      .then((res) => {
        const name = res.data.planName;
        const limit = resolvePlanLimit(name, res.data.planLimit);
        const used = res.data.usedEmails ?? 0;
        setCurrentPlan({
          name,
          limit,
          used,
        });
        setIsSubscription(res.data.isSubscription || false);
        setSubscriptionInfo(res.data.subscriptionInfo);
        setPlanPurchasedDate(
          res.data.planPurchasedDate
            ? new Date(res.data.planPurchasedDate)
            : null,
        );
      })
      .catch((err) => console.error("Failed to fetch current plan:", err));

    axios
      .get("/api/billing/history", { withCredentials: true })
      .then((res) => setBillingHistory(res.data))
      .catch((err) => console.error("Failed to fetch billing history:", err));
  }, []);

  // Calculate purchase date using saved planPurchasedDate or subscription period
  let purchaseDate;
  if (isSubscription && subscriptionInfo) {
    // For subscriptions, use the start of current period
    purchaseDate = new Date(subscriptionInfo.current_period_start);
  } else if (planPurchasedDate) {
    // Use saved plan purchase date
    purchaseDate = planPurchasedDate;
  } else if (user?.planPurchasedDate) {
    // Fallback: use from user object
    purchaseDate = new Date(user.planPurchasedDate);
  } else {
    // Final fallback: use account creation date
    purchaseDate = new Date(user.createdAt);
  }
  const purchaseDateString = purchaseDate
    ? purchaseDate.toLocaleDateString()
    : "N/A";

  // Calculate next payment date and determine if payment is due soon
  let nextPaymentDate;
  if (isSubscription && subscriptionInfo) {
    nextPaymentDate = new Date(subscriptionInfo.current_period_end);
  } else if (purchaseDate) {
    // Находим следующий месяц с тем же днем, который еще не наступил
    const today = new Date();
    const dayOfMonth = purchaseDate.getDate();
    nextPaymentDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      dayOfMonth,
    );
    // Если этот день уже прошел в текущем месяце, берем следующий месяц
    if (nextPaymentDate <= today) {
      nextPaymentDate.setMonth(nextPaymentDate.getMonth() + 1);
    }
  } else {
    nextPaymentDate = null;
  }
  const nextPaymentDateString = nextPaymentDate
    ? nextPaymentDate.toLocaleDateString()
    : "N/A";

  // Calculate renewal date for Basic plan - based on registration date
  const today = new Date();
  const userCreatedAt = new Date(user.createdAt);
  const basicPlanRenewalDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    userCreatedAt.getDate(),
  );

  // If today's date has passed the renewal day this month, move to next month
  if (today.getDate() > userCreatedAt.getDate()) {
    basicPlanRenewalDate.setMonth(basicPlanRenewalDate.getMonth() + 1);
  }

  const basicPlanRenewalDateString = basicPlanRenewalDate.toLocaleDateString();

  // Check if payment is due soon (within 7 days)
  const daysDifference = nextPaymentDate
    ? Math.ceil((nextPaymentDate - today) / (1000 * 60 * 60 * 24))
    : null;
  const isPaymentDueSoon =
    daysDifference !== null && daysDifference <= 7 && daysDifference > 0;
  const isPaymentOverdue =
    daysDifference !== null &&
    daysDifference < 0 &&
    currentPlan?.name !== "Basic";

  const userStatus = user?.status;
  const planExpired =
    currentPlan &&
    currentPlan.limit !== Infinity &&
    currentPlan.used >= currentPlan.limit;
  const approachingLimit =
    currentPlan &&
    currentPlan.limit !== Infinity &&
    currentPlan.used >= currentPlan.limit * 0.9 &&
    currentPlan.used < currentPlan.limit;
  const isBasicPlanExhausted = currentPlan?.name === "Basic" && planExpired;

  let accountStatusVariant = "success";
  let statusText, StatusIcon;
  if (userStatus === "Block") {
    accountStatusVariant = "error";
    statusText = "User Blocked";
    StatusIcon = BlockIcon;
  } else if (userStatus === "Moderation") {
    accountStatusVariant = "warning";
    statusText = "Account under moderation";
    StatusIcon = WarningIcon;
  } else if (
    (planExpired && currentPlan?.name !== "Basic") ||
    (isPaymentOverdue && currentPlan?.name !== "Basic")
  ) {
    accountStatusVariant = "error";
    statusText = planExpired ? "Plan expired" : "Payment overdue";
    StatusIcon = WarningIcon;
  } else if (isBasicPlanExhausted) {
    accountStatusVariant = "warning";
    statusText = "Basic plan limit reached";
    StatusIcon = WarningIcon;
  } else if (isPaymentDueSoon && currentPlan?.name !== "Basic") {
    accountStatusVariant = "warning";
    statusText = "Payment due soon";
    StatusIcon = WarningIcon;
  } else if (approachingLimit) {
    accountStatusVariant = "warning";
    statusText = "Approaching limit";
    StatusIcon = WarningIcon;
  } else {
    accountStatusVariant = "success";
    statusText = "Plan active";
    StatusIcon = CheckCircleIcon;
  }

  const accountStatusIconColor =
    accountStatusVariant === "success"
      ? acme.colors.brand
      : accountStatusVariant === "error"
        ? acme.colors.destructive
        : acme.colors.warning;

  // Calculate email usage percentage for progress indicator
  const usagePercent =
    currentPlan && currentPlan.limit && currentPlan.limit !== Infinity
      ? Math.round((currentPlan.used / currentPlan.limit) * 100)
      : 0;

  const fetchVerifiedEmails = async () => {
    try {
      const response = await axios.get("/api/emails/verified");

      // Проверяем, что response.data - это массив
      if (Array.isArray(response.data)) {
        const verified = response.data
          .filter((email) => email.isVerified)
          .map((email) => ({
            email: email.email,
            avatarUrl: email.avatarUrl || "",
            avatarPath: email.avatarPath || "",
          }));
        setVerifiedEmails(verified);
      } else if (response.data && typeof response.data === "object") {
        // Если response.data - это объект, возможно emails находятся в свойстве
        const dataArray = response.data.emails || response.data.data || [];

        if (Array.isArray(dataArray)) {
          const verified = dataArray
            .filter((email) => email.isVerified)
            .map((email) => ({
              email: email.email,
              avatarUrl: email.avatarUrl || "",
              avatarPath: email.avatarPath || "",
            }));
          setVerifiedEmails(verified);
        } else {
          // Если структура совсем другая, выводим данные в консоль для отладки
          console.log("Unexpected response structure:", response.data);
          setVerifiedEmails([]);
        }
      } else {
        console.log("Invalid response format:", response.data);
        setVerifiedEmails([]);
      }
    } catch (error) {
      console.error("Failed to fetch verified emails:", error);
      setErrorMessage(
        "Failed to load verified sender emails. Please try again later.",
      );
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get("/api/templates");
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const handleSenderEmailChange = (e) => {
    const chosenEmail = e.target.value;
    const found = verifiedEmails.find((item) => item.email === chosenEmail);
    setEmailData((prev) => ({
      ...prev,
      senderEmail: chosenEmail,
      senderAvatar: found?.avatarUrl || "",
    }));
  };

  const remainingEmails =
    currentPlan && currentPlan.limit !== Infinity
      ? currentPlan.limit - currentPlan.used
      : Infinity;

  const selectedContacts = groups
    .filter((group) => emailData.groups.includes(group.groupName))
    .reduce((sum, group) => sum + (group.sendableCount || 0), 0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleGroupChange = (e) => {
    const { value } = e.target;
    setEmailData((prevData) => ({
      ...prevData,
      groups: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setEmailData((prevData) => ({
      ...prevData,
      files: [...prevData.files, ...selectedFiles],
    }));
  };

  const handleRemoveFile = (index) => {
    setEmailData((prevData) => {
      const updatedFiles = [...prevData.files];
      updatedFiles.splice(index, 1);
      return { ...prevData, files: updatedFiles };
    });
  };

  const getEmailsFromGroups = async (selectedGroups) => {
    try {
      const emails = [];
      for (const groupName of selectedGroups) {
        const response = await axios.get(
          `/api/contacts/groups/${encodeURIComponent(groupName)}?sendableOnly=1`,
        );
        if (response.data && response.data.length > 0) {
          emails.push(...response.data);
        }
      }
      return emails;
    } catch (error) {
      console.error("Failed to fetch emails from groups:", error);
      return [];
    }
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Add useEffect to fetch user profile data
  useEffect(() => {
    // Fetch user profile
    axios
      .get("/api/users/me")
      .then((res) => {
        setUserProfile(res.data);
      })
      .catch((err) => console.error("Failed to fetch user profile:", err));

    // Fetch billing info if available
    axios
      .get("/api/billing/info")
      .then((res) => {
        setBillingInfo(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch billing info:", err);
        // Billing info might not be available for all users
        setBillingInfo(null);
      });
  }, []);

  const handleTermsClick = () => {
    setTermsDialogOpen(true);
  };

  const handleTermsDialogClose = () => {
    setTermsDialogOpen(false);
  };

  const handleTermsSign = () => {
    setTermsAgreed(true);
    setTermsDialogOpen(false);
  };

  // Функция для создания PDF с соглашением
  const generateTermsAgreementPDF = async () => {
    try {
      // Формируем данные для запроса к API
      const data = {
        user: userProfile,
        billing: billingInfo,
        date: new Date().toISOString(),
      };

      // API call to generate and save the agreement PDF
      const response = await axios.post("/api/agreement/sign", data);

      if (response.data.success) {
        console.log(
          "Terms agreement signed and saved successfully:",
          response.data.filename,
        );
        return true;
      } else {
        console.error(
          "Failed to generate agreement PDF:",
          response.data.message,
        );
        setErrorMessage(
          "Failed to create agreement document. Please try again.",
        );
        return false;
      }
    } catch (error) {
      console.error("Error generating agreement PDF:", error);
      setErrorMessage("Failed to create agreement document. Please try again.");
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!termsAgreed) {
      setErrorMessage(
        "You must agree to the Terms of Service before sending emails.",
      );
      return;
    }

    if (
      !emailData.campaignName ||
      !emailData.senderName ||
      !emailData.senderEmail ||
      !emailData.subject ||
      !emailData.message ||
      emailData.groups.length === 0
    ) {
      setErrorMessage("Please fill in all required fields.");
      return;
    }

    if (
      !verifiedEmails.some(
        (verified) => verified.email === emailData.senderEmail,
      )
    ) {
      setErrorMessage(
        "The sender email is not verified. Please use a verified email.",
      );
      return;
    }

    // DNS Propagation Check - проверяем готовность домена перед отправкой
    const senderDomain = emailData.senderEmail.split("@")[1];
    setLoading(true);

    try {
      console.log(`Checking DNS propagation for domain: ${senderDomain}`);
      const propagationResponse = await axios.post(
        "/api/domains/check-readiness",
        {
          domain: senderDomain,
        },
      );

      setDnsPropagationStatus(propagationResponse.data);

      if (!propagationResponse.data.readyToSend) {
        setLoading(false);
        setShowDnsPropagationWarning(true);
        setErrorMessage(
          `Domain "${senderDomain}" is not ready for sending emails. ` +
            `DNS propagation: ${propagationResponse.data.propagationScore}%. ` +
            `Please wait ${propagationResponse.data.recommendedWaitTime} minutes before trying again.`,
        );
        return;
      }

      console.log(
        `✅ DNS propagation check passed for ${senderDomain} (${propagationResponse.data.propagationScore}%)`,
      );
    } catch (propagationError) {
      console.warn(
        "DNS propagation check failed, proceeding with caution:",
        propagationError,
      );
      // Не блокируем отправку если проверка propagation не удалась
    }

    const recipients = await getEmailsFromGroups(emailData.groups);
    setLoading(false);

    if (recipients.length === 0) {
      setErrorMessage(
        "No email addresses found in the selected groups or all contacts are unsubscribed.",
      );
      return;
    }

    // ✅ НОВАЯ СИСТЕМА: Предупреждения о производительности
    setRecipientsCount(recipients.length);

    // Показываем предупреждения для больших рассылок
    if (recipients.length > 1000) {
      const estimatedTime = Math.ceil(recipients.length / 500); // ~500 писем в минуту с новыми настройками
      setErrorMessage(
        `⚠️ Большая рассылка (${recipients.length} получателей)\n` +
          `Ожидаемое время обработки: ~${estimatedTime} минут\n` +
          `Система автоматически разделит рассылку на батчи для стабильной работы.`,
      );
    } else if (recipients.length > 500) {
      setErrorMessage(
        `📊 Средняя рассылка (${recipients.length} получателей)\n` +
          `Ожидаемое время: ~${Math.ceil(recipients.length / 500)} минут\n` +
          `Рассылка будет обработана оптимизированными батчами.`,
      );
    } else if (recipients.length > 100) {
      setErrorMessage(
        `⚡ Рассылка будет обработана батчами для оптимальной производительности (${recipients.length} получателей)`,
      );
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmSend = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      // Сперва создаем PDF с соглашением, если он не создался - выходим
      const agreementCreated = await generateTermsAgreementPDF();
      if (!agreementCreated) {
        setErrorMessage(
          "Failed to create agreement document. Please try again.",
        );
        setLoading(false);
        return;
      }

      const recipients = await getEmailsFromGroups(emailData.groups);

      // Всегда применяем prepareTemplate для отправки email (без isPreviewMode для правильной обработки изображений)
      const combinedHtml = prepareTemplate(emailData.message, emailData.css);

      const data = {
        campaignName: emailData.campaignName,
        recipients: recipients,
        senderName: emailData.senderName,
        senderEmail: emailData.senderEmail,
        senderAvatar: emailData.senderAvatar,
        subject: emailData.subject,
        message: combinedHtml,
        draftCampaignId: draftId || undefined,
      };

      if (emailData.files && emailData.files.length > 0) {
        const attachments = await Promise.all(
          emailData.files.map(async (file) => {
            const base64Content = await readFileAsBase64(file);
            return {
              filename: file.name,
              content: base64Content,
              encoding: "base64",
            };
          }),
        );
        data.attachments = attachments;
      }

      console.log("Sending data:", data);
      await axios.post("/api/send-email", data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setSuccessMessage("Email sent successfully!");
      setEmailData(emptyEmailData);
      setSelectedTemplateId("");
      setDraftId(null);
      setDraftLoaded(false);
      setPendingDraft(null);
    } catch (error) {
      console.error(
        "Failed to send email:",
        error.response?.data || error.message,
      );
      setErrorMessage(
        `Failed to send email: ${error.response?.data?.error || error.message}`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSend = () => {
    setConfirmDialogOpen(false);
  };

  const handleTemplateSelect = (template) => {
    if (!template) {
      setSelectedTemplateId("");
      setEmailData((prev) => ({ ...prev, message: "", css: "" }));
      setTemplateSelectOpen(false);
      return;
    }

    setSelectedTemplateId(template._id);
    // Сохраняем оригинальный HTML для preview, обработка для email будет при отправке
    setEmailData((prev) => ({
      ...prev,
      message: template.html,
      css: template.css,
    }));
    setTemplateSelectOpen(false);
  };

  const handleSectionToggle = (section) => (_, isExpanded) => {
    setExpandedSections((prev) => ({ ...prev, [section]: isExpanded }));
  };

  const handleDesignMenuOpen = (event) => {
    event.stopPropagation();
    setDesignMenuAnchor(event.currentTarget);
  };

  const handleDesignMenuClose = () => {
    setDesignMenuAnchor(null);
  };

  const hasCampaignName = Boolean(emailData.campaignName?.trim());
  const hasSender = Boolean(
    emailData.senderName?.trim() && emailData.senderEmail,
  );
  const hasRecipients = emailData.groups.length > 0;
  const hasSubject = Boolean(emailData.subject?.trim());
  const hasDesign = Boolean(selectedTemplateId);
  const isCampaignNameTyping = Boolean(emailData.campaignName);

  return (
    <PageShell>
      <PageHeader
        title="Create Campaign"
        description="Create and send professional email campaigns to your audience. Build engaging content with our templates and track your results."
        sx={{ mb: 2 }}
      />

      {pendingDraft && !draftLoaded && (
        <UiCard
          sx={acmeDraftCardSx}
          onClick={() => loadDraftIntoForm(pendingDraft)}
        >
          <UiCardContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              py: 2,
              "&:last-child": { pb: 2 },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Continue your draft
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: acme.colors.mutedForegroundSolid, mt: 0.5 }}
              >
                You started &ldquo;{pendingDraft.campaignName}&rdquo; but
                didn&apos;t finish. Click here to continue where you left off.
              </Typography>
            </Box>
            <AcmeButton
              size="small"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleDiscardDraft(pendingDraft._id);
              }}
              sx={{ flexShrink: 0 }}
            >
              Discard
            </AcmeButton>
          </UiCardContent>
        </UiCard>
      )}

      {/* Plan Status Card */}
      {user && !isAdminRole(user.role) && currentPlan && (
        <UiCard
          sx={{
            mb: 3,
            boxShadow: "none",
            ...(accountStatusVariant === "success"
              ? acmeInfoCardActiveSx("plan")
              : {
                  borderColor:
                    accountStatusVariant === "error"
                      ? acme.colors.destructive
                      : acme.colors.warning,
                }),
          }}
        >
          <UiCardContent sx={acmeBillingSectionContentSx}>
            <Box
              sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}
            >
              <Avatar
                sx={{
                  bgcolor: acme.colors.brandMuted,
                  width: 48,
                  height: 48,
                }}
              >
                <StatusIcon
                  sx={{ color: accountStatusIconColor, fontSize: "1.5rem" }}
                />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: acme.colors.foreground }}
                >
                  {statusText}: {currentPlan.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: acme.colors.mutedForegroundSolid, mt: 0.5 }}
                >
                  {currentPlan.name === "Basic" ? (
                    <>
                      Free plan · Next upgrade: {basicPlanRenewalDateString}
                      {isBasicPlanExhausted && (
                        <AcmeChip
                          label="Upgrade recommended"
                          size="small"
                          sx={{
                            ml: 1,
                            bgcolor: acme.colors.brandMuted,
                            color: acme.colors.brand,
                            border: "none",
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      Purchased: {purchaseDateString} · Next payment:{" "}
                      {nextPaymentDateString}
                      {isPaymentDueSoon && (
                        <AcmeChip
                          label={`Due in ${daysDifference} days`}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {isPaymentOverdue && (
                        <AcmeChip
                          label="Overdue"
                          size="small"
                          sx={{
                            ml: 1,
                            bgcolor: acme.colors.destructive,
                            color: "#fff",
                            border: "none",
                          }}
                        />
                      )}
                    </>
                  )}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: acme.colors.foreground, fontWeight: 500 }}
              >
                {currentPlan.used} /{" "}
                {currentPlan.limit !== Infinity ? currentPlan.limit : "∞"}{" "}
                emails used
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: acme.colors.mutedForegroundSolid }}
              >
                {currentPlan.limit !== Infinity
                  ? `${(100 - usagePercent).toFixed(1)}% remaining`
                  : "Unlimited"}
              </Typography>
            </Box>
            {currentPlan.limit !== Infinity && (
              <LinearProgress
                variant="determinate"
                value={Math.min(usagePercent, 100)}
                sx={{
                  height: 8,
                  borderRadius: acme.radius.sm,
                  bgcolor: acme.colors.muted,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      usagePercent >= 100
                        ? acme.colors.destructive
                        : usagePercent >= 90
                          ? acme.colors.warning
                          : acme.colors.brand,
                  },
                }}
              />
            )}
          </UiCardContent>
        </UiCard>
      )}

      {/* Campaign Form */}
      <Box sx={{ mx: "auto" }}>
        <UiCard sx={{ ...acmePageSectionCardSx, boxShadow: "none", mt: 0 }}>
          <UiCardContent sx={acmeBillingSectionContentSx}>
            <PageBlockTitle gutterBottom>Create Campaign</PageBlockTitle>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={formSectionsStackSx}
            >
              <Box sx={campaignNameHeroSx}>
                <Box sx={campaignNameInnerSx(isCampaignNameTyping)}>
                  {!isCampaignNameTyping && (
                    <>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "1.125rem", sm: "1.25rem" },
                          color: acme.colors.foreground,
                          mb: 1,
                        }}
                      >
                        Name your campaign
                      </Typography>
                      <Typography
                        sx={{
                          color: acme.colors.mutedForegroundSolid,
                          fontSize: "0.875rem",
                          mb: 3,
                          lineHeight: 1.5,
                        }}
                      >
                        Enter a name to unlock sender, recipients, subject, and
                        design settings.
                      </Typography>
                    </>
                  )}
                  <Input
                    label="Campaign name"
                    name="campaignName"
                    value={emailData.campaignName}
                    onChange={handleChange}
                    required
                    size="medium"
                    autoFocus={!isCampaignNameTyping}
                    sx={campaignNameInputSx}
                  />
                </Box>
              </Box>

              {hasCampaignName && (
                <>
                  {/* Sender */}
                  <Accordion
                    expanded={expandedSections.sender}
                    onChange={handleSectionToggle("sender")}
                    sx={accordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <AccordionSectionSummary
                        icon={<EmailIcon sx={sectionIconSx} />}
                        title="Sender"
                        complete={hasSender}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={formFieldsStackSx}>
                        <Input
                          label="Sender name"
                          name="senderName"
                          value={emailData.senderName}
                          onChange={handleChange}
                        />
                        <AcmeSelect
                          label="Sender email"
                          name="senderEmail"
                          value={emailData.senderEmail}
                          onChange={handleSenderEmailChange}
                        >
                          {verifiedEmails.length > 0 ? (
                            verifiedEmails.map(({ email, avatarUrl }) => (
                              <MenuItem
                                key={email}
                                value={email}
                                sx={{
                                  "&:hover": {
                                    backgroundColor: acme.colors.muted,
                                  },
                                }}
                              >
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  {avatarUrl && (
                                    <Avatar
                                      src={avatarUrl}
                                      alt="avatar"
                                      sx={{ width: 24, height: 24, mr: 1.5 }}
                                    />
                                  )}
                                  <Typography variant="body2">
                                    {email}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled>
                              No verified emails found
                            </MenuItem>
                          )}
                        </AcmeSelect>
                        {emailData.senderEmail && (
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                mb: 1,
                                fontWeight: 600,
                                color: acme.colors.foreground,
                              }}
                            >
                              DNS Propagation Status
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                color: acme.colors.mutedForegroundSolid,
                              }}
                            >
                              Before sending your campaign, we check if your
                              domain&apos;s DNS records are properly propagated
                              across multiple DNS servers worldwide.
                            </Typography>
                            <DnsPropagationCheckSimple
                              domain={emailData.senderEmail.split("@")[1]}
                              onStatusChange={(status) => {
                                setDnsPropagationStatus(status);
                                if (
                                  status.readyToSend &&
                                  showDnsPropagationWarning
                                ) {
                                  setShowDnsPropagationWarning(false);
                                  setErrorMessage("");
                                }
                              }}
                            />
                            {showDnsPropagationWarning &&
                              dnsPropagationStatus && (
                                <Box
                                  sx={{
                                    p: 2,
                                    mt: 2,
                                    border: `1px solid ${acme.colors.warning}`,
                                    borderRadius: acme.radius.md,
                                    backgroundColor: "rgba(245, 158, 11, 0.08)",
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: acme.colors.warning,
                                      mb: 1,
                                    }}
                                  >
                                    Domain Not Ready for Sending
                                  </Typography>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    Your domain{" "}
                                    <strong>
                                      {emailData.senderEmail.split("@")[1]}
                                    </strong>{" "}
                                    is not fully propagated yet.
                                  </Typography>
                                  <Typography variant="body2" component="div">
                                    • <strong>Current propagation:</strong>{" "}
                                    {dnsPropagationStatus.propagationScore}%
                                    <br />• <strong>Required:</strong> 7/8 DNS
                                    servers
                                    <br />•{" "}
                                    <strong>Recommended wait time:</strong>{" "}
                                    {dnsPropagationStatus.recommendedWaitTime}{" "}
                                    minutes
                                  </Typography>
                                </Box>
                              )}
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Recipients */}
                  <Accordion
                    expanded={expandedSections.recipients}
                    onChange={handleSectionToggle("recipients")}
                    sx={accordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <AccordionSectionSummary
                        icon={<GroupIcon sx={sectionIconSx} />}
                        title="Recipients"
                        complete={hasRecipients}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={formFieldsStackSx}>
                        <AcmeSelect
                          label="Groups"
                          multiple
                          value={emailData.groups}
                          onChange={handleGroupChange}
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return "No groups selected";
                            }
                            const selectedGroups = groups.filter((g) =>
                              selected.includes(g.groupName),
                            );
                            const totalSendable = selectedGroups.reduce(
                              (sum, g) => sum + (g.sendableCount || 0),
                              0,
                            );
                            const groupNames = selectedGroups
                              .map((g) => g.groupName)
                              .join(", ");
                            return `${groupNames} (sendable: ${totalSendable})`;
                          }}
                        >
                          {groups.length > 0 ? (
                            groups.map((group) => {
                              const selectable =
                                group.campaignSelectable === true;
                              const status =
                                group.verificationStatus || "ready";
                              const label = selectable
                                ? `${group.groupName} (${group.sendableCount ?? 0} sendable / ${group.totalCount ?? 0} total)`
                                : `${group.groupName} — ${
                                    status === "verifying" ||
                                    status === "queued"
                                      ? `verifying ${group.verificationProgress ?? 0}%`
                                      : status === "failed"
                                        ? "verification failed"
                                        : "not ready"
                                  } (${group.validCount ?? 0} valid / ${group.totalCount ?? 0} total)`;
                              return (
                                <MenuItem
                                  key={group.groupName}
                                  value={group.groupName}
                                  disabled={!selectable}
                                  sx={{
                                    opacity: selectable ? 1 : 0.55,
                                    "&:hover": {
                                      backgroundColor: acme.colors.muted,
                                    },
                                  }}
                                >
                                  <Checkbox
                                    checked={
                                      emailData.groups.indexOf(
                                        group.groupName,
                                      ) > -1
                                    }
                                    disabled={!selectable}
                                    sx={checkboxSx}
                                  />
                                  <ListItemText primary={label} />
                                </MenuItem>
                              );
                            })
                          ) : (
                            <MenuItem disabled>No groups found</MenuItem>
                          )}
                        </AcmeSelect>
                        {currentPlan &&
                          currentPlan.limit !== Infinity &&
                          (() => {
                            const selectedGroups = groups.filter((g) =>
                              emailData.groups.includes(g.groupName),
                            );
                            const totalContacts = selectedGroups.reduce(
                              (sum, g) => sum + (g.sendableCount || 0),
                              0,
                            );
                            const remaining =
                              currentPlan.limit - currentPlan.used;
                            if (totalContacts > remaining) {
                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 1,
                                  }}
                                >
                                  <WarningIcon
                                    sx={{
                                      color: acme.colors.destructive,
                                      mr: 1,
                                      fontSize: "1.125rem",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: acme.colors.destructive }}
                                  >
                                    The number of contacts exceeds the plan
                                    limit.
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          })()}
                        {currentPlan &&
                          currentPlan.limit !== Infinity &&
                          currentPlan.used >= currentPlan.limit && (
                            <Typography
                              variant="body2"
                              sx={{ color: acme.colors.destructive }}
                            >
                              Your plan has been exhausted, please pay to
                              continue.
                            </Typography>
                          )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Subject */}
                  <Accordion
                    expanded={expandedSections.subject}
                    onChange={handleSectionToggle("subject")}
                    sx={accordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <AccordionSectionSummary
                        icon={<EditIcon sx={sectionIconSx} />}
                        title="Subject"
                        complete={hasSubject}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Input
                        label="Subject"
                        name="subject"
                        value={emailData.subject}
                        onChange={handleChange}
                        placeholder="Enter an engaging subject line..."
                      />
                    </AccordionDetails>
                  </Accordion>

                  {/* Design */}
                  <Accordion
                    expanded={expandedSections.design}
                    onChange={handleSectionToggle("design")}
                    sx={accordionSx}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <AccordionSectionSummary
                        icon={<GridViewIcon sx={sectionIconSx} />}
                        title="Design"
                        complete={hasDesign}
                        action={
                          <TableActionsMenuButton
                            ariaLabel="Design options"
                            onClick={handleDesignMenuOpen}
                            sx={{ color: acme.colors.mutedForegroundSolid }}
                          />
                        }
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={formFieldsStackSx}>
                        {selectedTemplateId ? (
                          <UiCard
                            sx={{
                              boxShadow: "none",
                              bgcolor: acme.colors.muted,
                            }}
                          >
                            <UiCardContent
                              sx={{ p: 2, "&:last-child": { pb: 2 } }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    minWidth: 0,
                                  }}
                                >
                                  <CheckCircleIcon
                                    sx={{
                                      color: acme.colors.brand,
                                      mr: 1,
                                      fontSize: "1.125rem",
                                    }}
                                  />
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {templates.find(
                                      (t) => t._id === selectedTemplateId,
                                    )?.name || "Unknown template"}
                                  </Typography>
                                </Box>
                                <AcmeButton
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleTemplateSelect(null)}
                                >
                                  Remove
                                </AcmeButton>
                              </Box>
                            </UiCardContent>
                          </UiCard>
                        ) : (
                          <Box
                            sx={{
                              border: `2px dashed ${acme.colors.border}`,
                              borderRadius: acme.radius.md,
                              p: 3,
                              textAlign: "center",
                              backgroundColor: acme.colors.muted,
                            }}
                          >
                            <GridViewIcon
                              sx={{
                                fontSize: "2rem",
                                color: acme.colors.mutedForegroundSolid,
                                mb: 1,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: acme.colors.mutedForegroundSolid }}
                            >
                              No template selected
                            </Typography>
                          </Box>
                        )}
                        <AcmeButton
                          variant="outline"
                          onClick={() => setTemplateSelectOpen(true)}
                          startIcon={
                            <GridViewIcon
                              sx={{ fontSize: "1.125rem !important" }}
                            />
                          }
                          fullWidth
                        >
                          {selectedTemplateId
                            ? "Change Template"
                            : "Choose Template"}
                        </AcmeButton>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Legal Agreement */}
                  <Accordion
                    expanded={expandedSections.legal}
                    onChange={handleSectionToggle("legal")}
                    sx={{ ...accordionSx }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <AccordionSectionSummary
                        icon={<GavelIcon sx={sectionIconSx} />}
                        title="Legal Agreement Required"
                        complete={termsAgreed}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        sx={{
                          color: acme.colors.mutedForegroundSolid,
                          lineHeight: 1.6,
                          mb: 2,
                        }}
                      >
                        By using Mailsball, you confirm compliance with all
                        applicable email marketing laws (GDPR, CAN-SPAM Act,
                        CASL). Mailsball is not responsible for the content of
                        your emails or any legal consequences arising from their
                        use.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: 2,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={termsAgreed}
                              onChange={(e) => setTermsAgreed(e.target.checked)}
                              sx={checkboxSx}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              I agree to the Terms of Service
                            </Typography>
                          }
                        />
                        <AcmeButton
                          variant="outline"
                          size="sm"
                          onClick={handleTermsClick}
                          startIcon={
                            <GavelIcon sx={{ color: acme.colors.warning }} />
                          }
                          sx={{
                            borderColor: acme.colors.warning,
                            color: acme.colors.warning,
                          }}
                        >
                          Read Terms
                        </AcmeButton>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Send Campaign */}
                  <Box sx={{ mt: 1.5 }}>
                    {userStatus === "Block" && (
                      <Typography
                        variant="body2"
                        color="error"
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <BlockIcon sx={{ mr: 1, fontSize: "1rem" }} />
                        Your account is blocked. Please contact support for
                        assistance.
                      </Typography>
                    )}
                    {userStatus === "Moderation" && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.warning,
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <WarningIcon sx={{ mr: 1, fontSize: "1rem" }} />
                        Your account is under moderation. Email sending is
                        temporarily unavailable.
                      </Typography>
                    )}
                    <Tooltip
                      title={
                        !termsAgreed
                          ? "You must agree to the Terms of Service before sending emails"
                          : ""
                      }
                      placement="top"
                    >
                      <span>
                        <AcmeButton
                          type="submit"
                          startIcon={<SendIcon />}
                          fullWidth
                          disabled={
                            loading ||
                            userStatus === "Block" ||
                            userStatus === "Moderation" ||
                            !termsAgreed ||
                            (!isAdminRole(user?.role) &&
                              currentPlan &&
                              (remainingEmails <= 0 ||
                                selectedContacts > remainingEmails))
                          }
                          sx={{ py: 1.25, fontSize: "1rem" }}
                        >
                          {loading ? "Sending..." : "Send Campaign"}
                        </AcmeButton>
                      </span>
                    </Tooltip>
                  </Box>
                </>
              )}
            </Box>
          </UiCardContent>
        </UiCard>
      </Box>

      <TableActionsMenu
        anchorEl={designMenuAnchor}
        open={Boolean(designMenuAnchor)}
        onClose={handleDesignMenuClose}
      >
        <UiMenuItem
          onClick={() => {
            handleDesignMenuClose();
            setTemplatePreviewDialogOpen(true);
          }}
        >
          <VisibilityIcon
            sx={{
              mr: 1.5,
              fontSize: "1.125rem",
              color: acme.colors.mutedForegroundSolid,
            }}
          />
          Template Preview
        </UiMenuItem>
        <UiMenuItem
          onClick={() => {
            handleDesignMenuClose();
            setEmailContentDialogOpen(true);
          }}
        >
          <EditIcon
            sx={{
              mr: 1.5,
              fontSize: "1.125rem",
              color: acme.colors.mutedForegroundSolid,
            }}
          />
          Email Content
        </UiMenuItem>
        <UiMenuItem
          onClick={() => {
            handleDesignMenuClose();
            setAttachmentsDialogOpen(true);
          }}
        >
          <AttachFileIcon
            sx={{
              mr: 1.5,
              fontSize: "1.125rem",
              color: acme.colors.mutedForegroundSolid,
            }}
          />
          Attachments
        </UiMenuItem>
      </TableActionsMenu>

      <UiDialog
        open={emailContentDialogOpen}
        onClose={() => setEmailContentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitleText
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          Email Content
          {user && normalizeRole(user.role) === ROLE_USER && (
            <AcmeButton
              variant="outline"
              size="sm"
              startIcon={<EditIcon />}
              onClick={handleEditTemplate}
            >
              Edit Template
            </AcmeButton>
          )}
        </DialogTitleText>
        <DialogBody>
          <Input
            label="HTML Content"
            name="message"
            value={emailData.message}
            onChange={handleChange}
            multiline
            rows={12}
            disabled={user && normalizeRole(user.role) === ROLE_USER}
            placeholder="Your email HTML content will appear here..."
            sx={{
              "& .MuiOutlinedInput-root textarea": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />
        </DialogBody>
        <DialogFooter>
          <AcmeButton
            variant="outline"
            onClick={() => setEmailContentDialogOpen(false)}
          >
            Close
          </AcmeButton>
        </DialogFooter>
      </UiDialog>

      <UiDialog
        open={attachmentsDialogOpen}
        onClose={() => setAttachmentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitleText>Attachments</DialogTitleText>
        <DialogBody>
          <Typography
            variant="body2"
            sx={{ color: acme.colors.mutedForegroundSolid, mb: 2 }}
          >
            Add files to your email campaign. Supported formats: PDF, DOC, DOCX,
            XLS, XLSX, images.
          </Typography>
          <AcmeButton
            variant="outline"
            component="label"
            startIcon={<CloudUploadIcon />}
            fullWidth
            sx={{
              py: 1.5,
              borderStyle: "dashed",
              borderWidth: 2,
            }}
          >
            Choose Files to Upload
            <input type="file" hidden multiple onChange={handleFileChange} />
          </AcmeButton>
          {emailData.files && emailData.files.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {emailData.files.map((file, index) => (
                <UiCard key={index} sx={{ mb: 1 }}>
                  <UiCardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                        }}
                      >
                        <AttachFileIcon
                          sx={{
                            color: acme.colors.mutedForegroundSolid,
                            mr: 1,
                            fontSize: "1rem",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 500 }}
                          noWrap
                        >
                          {file.name}
                        </Typography>
                      </Box>
                      <AcmeButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        sx={{
                          color: acme.colors.destructive,
                          minWidth: "auto",
                          ml: 1,
                        }}
                      >
                        Remove
                      </AcmeButton>
                    </Box>
                  </UiCardContent>
                </UiCard>
              ))}
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <AcmeButton
            variant="outline"
            onClick={() => setAttachmentsDialogOpen(false)}
          >
            Close
          </AcmeButton>
        </DialogFooter>
      </UiDialog>

      <CampaignTemplatePreviewDialog
        open={templatePreviewDialogOpen}
        onClose={() => setTemplatePreviewDialogOpen(false)}
        html={emailData.message}
        css={emailData.css}
        previewDevice={previewDevice}
        setPreviewDevice={setPreviewDevice}
      />

      <UiDialog
        open={confirmDialogOpen}
        onClose={handleCancelSend}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitleText>Confirm Send</DialogTitleText>
        <DialogBody>
          <Typography sx={{ color: acme.colors.mutedForegroundSolid }}>
            Do you want to send email to{" "}
            <strong>{recipientsCount.toLocaleString()}</strong>{" "}
            {recipientsCount === 1 ? "recipient" : "recipients"}?
          </Typography>
        </DialogBody>
        <DialogFooter>
          <AcmeButton variant="outline" onClick={handleCancelSend}>
            Cancel
          </AcmeButton>
          <AcmeButton onClick={handleConfirmSend} autoFocus>
            Send
          </AcmeButton>
        </DialogFooter>
      </UiDialog>

      <PageSnackbars
        {...snackbarProps}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />

      {/* Dialogs */}
      <TermsAgreementDialog
        open={termsDialogOpen}
        onClose={handleTermsDialogClose}
        onSign={handleTermsSign}
        userProfile={userProfile}
        billingInfo={billingInfo}
      />

      <TemplateSelectDialog
        open={templateSelectOpen}
        onClose={() => setTemplateSelectOpen(false)}
        templates={templates}
        onSelect={handleTemplateSelect}
      />
    </PageShell>
  );
};

export default Campaigns;
